import {
  GelBoxLayout,
  GelHeading5,
  GelHeading6,
  GelList,
  GelListItem,
  GelParagraph,
  GelScreenDetectorContext,
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import List from "components/components/List";
import Paragraph from "components/components/Paragraph";

import { useContext } from "react";
const AboutSkinCancer = () => {
  const { isLargerThanXsScreen, screen } = useContext(GelScreenDetectorContext);
  return (
    <>
      {/* <CardHeader
        title="Understanding skin cancer"
        description="Learn about the cancer different types and their causes."
        backgroundUrl="/images/about-skin-cancer.jpg"
      ></CardHeader> */}

      <GelHeading5
        as="h2"
        marginTop={getGelTokens().global.sizeBaseX4}
        marginBottom={getGelTokens().global.sizeBaseX6}
      >
              What is skin cancer?²
      </GelHeading5>
      <GelParagraph marginBottom={getGelTokens().global.sizeBaseX4}>
        Skin cancer occurs when skin cells are damaged, triggering the growth of abnormal cells in
        the skin.
      </GelParagraph>
      <GelParagraph marginBottom={getGelTokens().global.sizeBaseX4}>
        There are three main types of skin cancer:
      </GelParagraph>
      <GelList>
        <GelListItem>
          <GelParagraph>Basal Cell Carcinoma (BCC),</GelParagraph>
        </GelListItem>
        <GelListItem>
          <GelParagraph>Squamous Cell Carcinoma (SCC), and</GelParagraph>
        </GelListItem>
        <GelListItem>
          <GelParagraph>Melanoma – the most dangerous form of skin cancer.</GelParagraph>
        </GelListItem>
      </GelList>
      <GelParagraph marginTop={getGelTokens().global.sizeBaseX4}>
        Both Basal Cell Carcinoma and Squamous Cell Carcinoma are non-melanoma skin cancers, the
        most common skin cancers in Australia.
      </GelParagraph>

      <GelHeading5
        as="h2"
        marginTop={getGelTokens().global.sizeBaseX10}
        marginBottom={getGelTokens().global.sizeBaseX6}
      >
              What causes skin cancer?²
      </GelHeading5>
      <GelParagraph marginBottom={getGelTokens().global.sizeBaseX4}>
        Most skin cancer is caused by an overexposure to ultraviolet (UV) radiation, which is
        estimated to cause around 95% of melanoma cases and around 99% of non-melanoma skin
        cancers in Australia. While UV radiation is produced by the sun, it can also come from
        artificial lights used in solariums.
      </GelParagraph>
      <GelParagraph marginBottom={getGelTokens().global.sizeBaseX4}>
        Anyone can develop skin cancer, but it’s more common in older people. The risk is higher in
        people who:
      </GelParagraph>
      <GelList style={{ marginBottom: 40 }}>
        <GelListItem>
          <GelParagraph>
            Have already been diagnosed with skin cancer and/or have a family history of skin
            cancer,
          </GelParagraph>
        </GelListItem>
        <GelListItem>
          <GelParagraph>Have many moles on their skin,</GelParagraph>
        </GelListItem>
        <GelListItem>
          <GelParagraph>Have a skin type easily damaged by UV radiation,</GelParagraph>
        </GelListItem>
        <GelListItem>
          <GelParagraph>Have a history of bad sunburns,</GelParagraph>
        </GelListItem>
        <GelListItem>
          <GelParagraph>
            Spend lots of time outdoors or work outdoors, unprotected and/or
          </GelParagraph>
        </GelListItem>
        <GelListItem>
          <GelParagraph>Suntan, use or have used solariums or sunlamps.</GelParagraph>
        </GelListItem>
      </GelList>

      {/* Infographic block */}
      {/*
      <GelBoxLayout>
        <Stat1
          backgroundColor={getGelTokens().global.themeColorBackgroundLight}
          padding={40}
        />

        <Stat2
          inverse={getGelTokens().global.themeColorTextInverse}
          backgroundColor={getGelTokens().global.themeColorBackgroundInverse}
          padding={40}
        />
      </GelBoxLayout> */}

      <GelHeading5
        as="h2"
        marginTop={getGelTokens().global.sizeBaseX10}
        marginBottom={getGelTokens().global.sizeBaseX6}
      >
        Types of skin cancer
      </GelHeading5>
          <GelHeading6 marginBottom={getGelTokens().global.sizeBaseX6}>Melanoma² ³</GelHeading6>
      <GelBoxLayout
        gutter="xlarge"
        vertical={isLargerThanXsScreen(screen) ? false : true}
        space={[1, "auto"]}
        alignment="start"
        distribution="spaceBetween"
      >
        <Paragraph
          content={
            "Melanoma develops in the melanocytes. These cells produce melanin, the pigment that gives your skin its colour. When these cells develop DNA damage (which may be caused by UV light), they may grow out of control and can eventually form a mass of cancerous cells, which is called a melanoma.\n" +
            "\n" +
            "The main cause of melanoma is overexposure to ultraviolet (UV) radiation – like sunlight – although it can occur anywhere on the skin, including places that receive little or no sun exposure, like the soles of your feet or even inside the mouth.\n" +
            "\n" +
            "People with fair or freckled skin, lots of moles, a history of sunburn in childhood or adolescence, or a family history of melanoma may be more at risk.\n" +
            "\n" +
            "Melanoma can grow quickly. Untreated, it can spread deeper into the skin and be carried to other parts of the body (like the lungs, liver and brain) via lymphatic or blood vessels.\n"
          }
        />

        <img
          src={process.env.PUBLIC_URL + "/images/spots/melanoma.png"}
          alt="what a melanoma might look like"
        ></img>
      </GelBoxLayout>

      <GelHeading6
        marginTop={getGelTokens().global.sizeBaseX10}
        marginBottom={getGelTokens().global.sizeBaseX6}
      >
              Basal cell carcinoma⁴
      </GelHeading6>
      <GelBoxLayout
        gutter="xlarge"
        vertical={isLargerThanXsScreen(screen) ? false : true}
        space={["auto", 1]}
        alignment="start"
        distribution="spaceBetween"
      >
        <img
          src={process.env.PUBLIC_URL + "/images/spots/basal-cell.png"}
          alt="what a basal cell carcinoma might look like"
        ></img>
        <>
          <Paragraph
            content={
              `Basal Cell Carcinoma (BCC) is the most common form of skin cancer, making up 70% of non-melanoma skin cancers. It is caused by abnormal, uncontrolled growth of basal cells in the lower layer of the epidermis (top, outer layer of the skin). It can appear anywhere on the body but most commonly develops on parts of the body that receive more high sun exposure (the head, face, neck, shoulders and back).\n` +
              "\n" +
              "BCC often has no symptoms and grows slowly without spreading to other parts of the body. When detected and treated early, they are the most curable and cause minimal damage.\n" +
              "\n" +
              "Symptoms of BCC can include:\n"
            }
          />
          <List
            content={
              "A pearly lump, and/or \n" +
              "A scaly, dry area that is shiny and pale or bright pink in colour. "
            }
          />
        </>
      </GelBoxLayout>
      <GelHeading6
        marginTop={getGelTokens().global.sizeBaseX10}
        marginBottom={getGelTokens().global.sizeBaseX6}
      >
              Squamous cell carcinoma⁴
      </GelHeading6>
      <GelBoxLayout
        gutter="xlarge"
        space={[1, "auto"]}
        vertical={isLargerThanXsScreen(screen) ? false : true}
        alignment="start"
        distribution="spaceBetween"
        style={{ marginBottom: getGelTokens().global.sizeBaseX14 }}
      >
        <>
          <Paragraph
            content={
              "Squamous Cell Carcinoma (SCC) is the second most common form of skin cancer, making up 30% of non-melanoma skin cancers. It is caused by abnormal, accelerated growth of squamous cells in the upper layer of the epidermis. It usually appears where the skin has had the most exposure to the sun (the head, neck, hands, forearms and lower legs).\n" +
              "\n" +
              "SCC generally grow quickly over weeks or months and symptoms may include:\n"
            }
          />
          <List
            content={
              "Thickened red, scaly spot,\n" +
              "Rapidly growing lump,\n" +
              "Looks like a sore that has not healed, and or\n" +
              "May be tender to touch."
            }
          />
        </>
        <img
          src={process.env.PUBLIC_URL + "/images/spots/squamous-cell.png"}
          alt="what a squamous cell carcinoma might look like"
        ></img>
      </GelBoxLayout>

      {/* <List
        ordered
        content={
          "ARMSTRONG BK, KRICKER A. HOW MUCH MELANOMA IS CAUSED BY SUN EXPOSURE? MELANOMA RES 1993;3(6):395-401.\n" +
          "ARMSTRONG BK. HOW SUN EXPOSURE CAUSES SKIN CANCER. IN: HILL D, ELWOOD JM, ENGLISH DR, EDS. PREVENTION OF SKIN CANCER. DORDRECHT, THE NETHERLANDS:: KLUWER ACADEMIC PUBLISHERS, 2004, PP. 89-116:PP.89-116\n" +
          "SUNSMART VICTORIA 2020.\n" +
          "STAPLES MP, ELWOOD M, BURTON RC, WILLIAMS JL, MARKS R, GILES GG. NON-MELANOMA SKIN CANCER IN AUSTRALIA: THE 2002 NATIONAL SURVEY AND TRENDS SINCE 1985. MED J AUST. 2006;184(1):6-10.\n" +
          "TAL SPOTCHECKER AUSTRALIAN SKIN SAFETY REPORT, 2021\n" +
          "MELANOMA INSTITUTE OF AUSTRALIA, 2019. WHAT IS MELANOMA?\n" +
          "CANCER COUNCIL AUSTRALIA\n" +
          "THE SKIN CANCER FOUNDATION, 2020. BASAL CELL CARCINOMA OVERVIEW\n" +
          "CANCER COUNCIL AUSTRALIA"
        }
      /> */}
    </>
  );
};

export default AboutSkinCancer;
